import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faExternalLinkAlt, faPlus, faTrash, faBuilding, faUser,
  faFlagCheckered, faSpinner, faChevronDown, faChevronRight, faLink, faCalendarAlt,
  faClipboardList, faCommentDots, faStickyNote, faEdit, faCertificate, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { closingDateBadge } from '../closingDateBadge';
import { useAuth } from '../../../contexts/AuthContext';
import SurveyProgressBars from './SurveyProgressBars';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import VagueBadge from './VagueBadge';
import PaymentRelanceCard from './PaymentRelanceCard';
import PaymentRelancePrompt from './PaymentRelancePrompt';

const STEP_LABELS = { setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing' };
const STEP_COLORS = { setup: '#d97706', terrain: '#059669', reporting: '#0284c7', closing: '#7c3aed' };

function getStorageKey(projectId, channelKey) {
  return `project_${projectId}_read_${channelKey}`;
}

const SlotQuickModal = ({ isOpen, onClose, slot, project, onUpdate }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [togglingFinished, setTogglingFinished] = useState(false);
  const [localFinished, setLocalFinished] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [finishError, setFinishError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [localNotes, setLocalNotes] = useState('');
  const [localAttestation, setLocalAttestation] = useState(null);
  const [savingAttestation, setSavingAttestation] = useState(false);
  const [sendingAtt, setSendingAtt] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [localSentAt, setLocalSentAt] = useState(null);
  const [localRelanceCount, setLocalRelanceCount] = useState(0);
  const [relanceState, setRelanceState] = useState(null);
  const [relancePrompt, setRelancePrompt] = useState(false);

  // Changer de carte sans fermer la modale ne remonte pas le composant : sans cette
  // resynchronisation en rendu, la carte affiche le délai de l'enquête précédente le temps
  // que l'effet d'ouverture passe.
  const [relanceSlotId, setRelanceSlotId] = useState(slot?.id || null);
  if (relanceSlotId !== (slot?.id || null)) {
    setRelanceSlotId(slot?.id || null);
    setRelanceState(slot?.payment_relance || null);
    setRelancePrompt(false);
  }

  const fetchChecklists = useCallback(async () => {
    if (!project?.id || !slot?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/checklists`, { params: { survey_slot_id: slot.id } });
      setChecklists(res.data.data || []);
      setChecklistLoaded(true);
    } catch { setChecklistLoaded(true); }
  }, [project?.id, slot?.id]);

  const fetchUnread = useCallback(async () => {
    if (!project?.id || !slot?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/messages/latest-per-channel`);
      const latest = res.data.data || {};
      const channelKey = String(slot.id);
      const latestId = latest[channelKey];
      if (latestId) {
        const stored = parseInt(localStorage.getItem(getStorageKey(project.id, channelKey)) || '0', 10);
        if (latestId > stored) setUnreadCount(1);
      }
    } catch { }
  }, [project?.id, slot?.id]);

  useEffect(() => {
    if (isOpen && slot) {
      setChecklistLoaded(false);
      setUnreadCount(0);
      setFinishError(null);
      fetchChecklists();
      fetchUnread();
      setLocalFinished(slot.is_finished || false);
      setLocalNotes(slot.notes || '');
      setEditingNotes(false);
      setNotesOpen(false);
      setLocalAttestation(slot.attestation_requested ?? null);
      setLocalSentAt(slot.attestation_request_sent_at || null);
      setLocalRelanceCount(slot.attestation_relance_count || 0);
      setSendError(null);
      setRelanceState(slot.payment_relance || null);
      setRelancePrompt(false);
    }
  }, [isOpen, slot, fetchChecklists, fetchUnread]);

  // Holds inline edits (notes, checklists, assignees, attestation): a stray backdrop
  // click must not discard them. Exit via ✕, Escape, or the explicit buttons.
  const dismiss = useModalDismiss(onClose, { backdropClose: false, enabled: isOpen && !!slot && !!project });
  // Destructive confirmation: require an explicit Annuler / ✕ / Escape.
  const deleteDismiss = useModalDismiss(() => setDeleteConfirm(null), { backdropClose: false, enabled: !!deleteConfirm });

  if (!isOpen || !slot || !project) return null;

  const isLinked = !!(slot.survey_id || slot.survey || slot.linked_surveys?.length);
  // One uniform list of links: pivot rows when present, else the legacy single survey.
  const linkRows = (slot.linked_surveys?.length || 0) > 0
    ? slot.linked_surveys
    : (slot.survey ? [{ id: 'primary', survey: slot.survey, survey_wave: slot.survey_wave }] : []);
  const attestationReceived = checklists.some(c => c.step === 'closing' && c.label === 'Attestation de référence' && c.is_completed);
  const isCati = slot.survey && slot.survey.poste_qcode !== 'ref';
  const stepColor = STEP_COLORS[slot.step] || '#757575';
  const assignees = (slot.assignees?.length > 0) ? slot.assignees.map(a => a.name).join(', ') : '';
  const doneCount = checklists.filter(i => i.is_completed).length;

  const handleAddChecklist = async () => {
    if (!newLabel.trim() || adding) return;
    setAdding(true);
    try {
      const res = await api.post(`/projects/${project.id}/checklists`, { step: slot.step || 'terrain', label: newLabel.trim(), survey_slot_id: slot.id });
      setChecklists(prev => [...prev, res.data.data]);
      setNewLabel('');
    } catch { }
    finally { setAdding(false); }
  };

  const handleToggle = async (itemId) => {
    setChecklists(prev => prev.map(i => i.id === itemId ? { ...i, is_completed: !i.is_completed, completed_by: !i.is_completed ? { name: 'Vous' } : null } : i));
    try {
      const res = await api.post(`/projects/${project.id}/checklists/${itemId}/toggle`);
      setChecklists(prev => prev.map(i => i.id === itemId ? res.data.data : i));
      applyClosingState(res.data.closing);
    }
    catch { fetchChecklists(); }
  };

  // Cocher « Facturation » fait basculer en attente de paiement : c'est le moment de demander
  // le délai accordé au client, tant qu'il est encore au défaut de 60 jours.
  const applyClosingState = (closing) => {
    if (!closing) return;
    setRelanceState(closing.payment_relance || null);
    if (closing.closing_step === 'attente_paiement' && closing.payment_relance?.is_default) {
      setRelancePrompt(true);
    }
  };

  const handleDelete = async (itemId) => {
    const backup = checklists;
    setChecklists(prev => prev.filter(i => i.id !== itemId));
    try { await api.delete(`/projects/${project.id}/checklists/${itemId}`); }
    catch { setChecklists(backup); }
    finally { setDeleteConfirm(null); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/projects/${project.id}/slots/${slot.id}/notes`, { notes: notesValue });
      setLocalNotes(notesValue);
      setEditingNotes(false);
      if (onUpdate) onUpdate();
    } catch { }
    finally { setSavingNotes(false); }
  };

  const handleSendAttestation = async () => {
    setSendingAtt(true); setSendError(null);
    try {
      const res = await api.post(`/projects/${project.id}/slots/${slot.id}/attestation-request`);
      setLocalSentAt(res.data.sent_at || new Date().toISOString());
      setLocalRelanceCount(res.data.relance_count ?? 0);
      if (onUpdate) onUpdate();
    } catch (err) {
      setSendError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally { setSendingAtt(false); }
  };

  const handleSetAttestation = async (requested) => {
    setSavingAttestation(true);
    try {
      await api.post(`/projects/${project.id}/slots/${slot.id}/attestation`, { requested });
      setLocalAttestation(requested);
      fetchChecklists();
      if (onUpdate) onUpdate();
    } catch { }
    finally { setSavingAttestation(false); }
  };

  const handleToggleFinished = async () => {
    const wasFinished = localFinished;
    setTogglingFinished(true);
    setFinishError(null);
    // Only optimistic when unmarking (no server validation)
    if (wasFinished) setLocalFinished(false);
    try {
      await api.post(`/projects/${project.id}/slots/${slot.id}/toggle-finished`);
      // On success, flip the state (for marking, this is where it actually changes)
      if (!wasFinished) setLocalFinished(true);
      if (onUpdate) onUpdate();
    } catch (err) {
      // Revert optimistic unmark
      if (wasFinished) setLocalFinished(true);
      const data = err.response?.data;
      setFinishError(data?.reasons ? data.reasons.join(' · ') : (data?.message || 'Erreur'));
    }
    finally { setTogglingFinished(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: stepColor + '18', color: stepColor }}>
                {STEP_LABELS[slot.step] || slot.step}
              </span>
              {isLinked && slot.survey && (
                <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: isCati ? '#f3e8ff' : '#dbeafe', color: isCati ? '#7c3aed' : '#0284c7' }}>
                  {isCati ? 'CATI' : 'F2F'}
                </span>
              )}
              {localFinished && (
                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#e6f4ea', color: '#137333' }}>TERMINÉE</span>
              )}
            </div>

            {/* Project name */}
            <div style={{ fontSize: '0.72rem', color: '#9e9e9e', marginBottom: 2 }}>
              <span style={{ color: '#e0e0e0', fontWeight: 700 }}>P</span> {project.name}
            </div>

            {/* Slot name */}
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#242424', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#e0e0e0', fontWeight: 700, fontSize: '0.85rem' }}>Q</span> {slot.name}
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: '0.72rem', color: '#757575', flexWrap: 'wrap' }}>
              {project.client && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.4 }} /> {project.client.name}
                </span>
              )}
              {assignees && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FontAwesomeIcon icon={faUser} size="xs" style={{ opacity: 0.4 }} /> {assignees}
                </span>
              )}
              {slot.wave && (
                <span style={{ color: '#bdbdbd' }}>Groupe: {slot.wave.name}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div style={{ padding: '0 24px 24px', overflowY: 'auto', flex: 1 }}>
          {finishError && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '8px 12px', marginBottom: 12, fontSize: '0.75rem', color: '#991b1b',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontWeight: 600 }}>!</span> {finishError}
            </div>
          )}
          {/* Linked survey info */}
          {isLinked && linkRows.length > 0 && (
            <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <FontAwesomeIcon icon={faLink} size="xs" style={{ color: '#bdbdbd' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {linkRows.length > 1 ? `Enquêtes liées (${linkRows.length})` : 'Enquête liée'}
                </span>
              </div>
              {/* Every linked survey renders identically — no "primary" special case. */}
              {linkRows.map((l, i) => (
                <div key={l.id} style={{
                  marginTop: i === 0 ? 0 : 10,
                  paddingTop: i === 0 ? 0 : 10,
                  borderTop: i === 0 ? 'none' : '1px solid #ececec',
                }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#242424', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {l.survey?.title}
                    <VagueBadge wave={l.survey_wave} style={{ fontSize: '0.6rem', padding: '2px 7px' }} />
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#9e9e9e', marginTop: 2 }}>LS: {l.survey?.limesurvey_id}</div>
                  {l.survey?.date_fin && (
                    <div style={{ fontSize: '0.72rem', color: '#757575', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.4 }} /> Date fin: {fmtDate(l.survey.date_fin)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Discussion counter */}
          <div
            onClick={() => { onClose(); navigate(`/projects/${project.id}?tab=brief`); }}
            style={{ ...counterChip, cursor: 'pointer', marginBottom: 14, position: 'relative' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
          >
            <FontAwesomeIcon icon={faCommentDots} size="sm" style={{ color: '#9e9e9e' }} />
            <span style={{ fontSize: '0.68rem', color: '#9e9e9e' }}>Discussion</span>
            {unreadCount > 0 && (
              <span style={{
                background: '#dc2626', color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                padding: '1px 6px', borderRadius: 10, marginLeft: 4,
              }}>
                Nouveau
              </span>
            )}
          </div>

          {/* Survey progress — one set per linked survey/vague, since progress is
              tracked per survey and a blended bar would be meaningless. */}
          {isLinked && user?.role !== 'resp_admin' && linkRows.map(l => (
            <div key={`prog-${l.id}`}>
              {linkRows.length > 1 && (
                <div style={{ fontSize: '0.66rem', color: '#9e9e9e', marginTop: 8, marginBottom: 2 }}>
                  {l.survey?.title}{l.survey_wave ? ` — ${l.survey_wave.label}` : ''}
                </div>
              )}
              <SurveyProgressBars surveyId={l.survey?.id} surveyWaveId={l.survey_wave?.id || null} />
            </div>
          ))}

          {/* Terminer toggle — only once encaissé */}
          {isLinked && slot.closing_step === 'encaisse' && (
            <button onClick={handleToggleFinished} disabled={togglingFinished}
              style={{
                width: '100%', padding: '9px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 14,
                border: localFinished ? '1px solid #137333' : '1px solid #e0e0e0',
                background: localFinished ? '#e6f4ea' : '#fff',
                color: localFinished ? '#137333' : '#424242',
                fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s', opacity: togglingFinished ? 0.5 : 1,
              }}>
              {togglingFinished ? <FontAwesomeIcon icon={faSpinner} size="sm" spin /> : <FontAwesomeIcon icon={faFlagCheckered} size="sm" />}
              {localFinished ? 'Marquée comme terminée' : 'Marquer comme terminée'}
            </button>
          )}

          {/* Livrables deadline (livrables step only) */}
          {slot.step === 'closing' && slot.closing_step === 'livrables' && (() => {
            const b = closingDateBadge(slot);
            return (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ color: '#d97706' }} />
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Deadline livrables</span>
                </div>
                {slot.livrables_deadline ? (
                  <span style={{ fontSize: '0.76rem', fontWeight: 600, color: b ? b.color : '#92400e' }}>
                    {new Date(slot.livrables_deadline).toLocaleDateString('fr-FR')}{b ? ` · ${b.text}` : ''}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.72rem', color: '#bdbdbd' }}>Non définie</span>
                )}
              </div>
            );
          })()}

          {/* Attestation de référence */}
          {slot.step === 'closing' && (
            <div style={{ background: localAttestation === true && attestationReceived ? '#f0fdf4' : '#fff7ed', border: `1px solid ${localAttestation === true && attestationReceived ? '#bbf7d0' : '#fed7aa'}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <FontAwesomeIcon icon={faCertificate} size="xs" style={{ color: localAttestation === true && attestationReceived ? '#059669' : '#ea580c' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: localAttestation === true && attestationReceived ? '#166534' : '#9a3412', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Attestation de référence
                </span>
              </div>
              {localAttestation === null ? (
                <div>
                  <div style={{ fontSize: '0.76rem', color: '#7c2d12', marginBottom: 8 }}>Demander une attestation de référence pour cette enquête ?</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleSetAttestation(true)} disabled={savingAttestation}
                      style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', background: '#ea580c', color: '#fff', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', opacity: savingAttestation ? 0.5 : 1 }}>Oui</button>
                    <button onClick={() => handleSetAttestation(false)} disabled={savingAttestation}
                      style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Non</button>
                  </div>
                </div>
              ) : localAttestation === true ? (
                attestationReceived ? (
                  <span style={{ fontSize: '0.76rem', color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="sm" /> Reçue
                  </span>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: '0.76rem', color: '#9a3412', fontWeight: 600 }}>
                        ✓ {localRelanceCount > 0 ? 'Relancé' : (localSentAt ? 'Demandé' : 'À demander')} (voir checklist)
                      </span>
                      <button onClick={() => handleSetAttestation(false)} disabled={savingAttestation}
                        style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>Annuler la demande</button>
                    </div>
                    {(() => {
                      const sentToday = localSentAt && new Date(localSentAt).toDateString() === new Date().toDateString();
                      const label = sendingAtt
                        ? 'Envoi…'
                        : sentToday
                          ? 'Envoyée aujourd\'hui'
                          : (localSentAt ? 'Envoyer relance' : 'Envoyer demande');
                      return (
                        <div>
                          {sendError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.7rem', padding: '6px 8px', borderRadius: 6, marginBottom: 6 }}>{sendError}</div>
                          )}
                          {localSentAt && !sentToday && (
                            <div style={{ fontSize: '0.66rem', color: '#9a3412', marginBottom: 6 }}>
                              Dernier envoi : {new Date(localSentAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                          <button onClick={handleSendAttestation} disabled={sendingAtt || sentToday}
                            style={{ width: '100%', padding: '7px 0', borderRadius: 6, border: 'none', fontSize: '0.74rem', fontWeight: 600, cursor: (sendingAtt || sentToday) ? 'default' : 'pointer', background: sentToday ? '#e5e7eb' : '#ea580c', color: sentToday ? '#9ca3af' : '#fff', opacity: sendingAtt ? 0.6 : 1 }}>
                            {label}
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.76rem', color: '#9ca3af' }}>Non demandée</span>
                  <button onClick={() => handleSetAttestation(true)} disabled={savingAttestation}
                    style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #ea580c', background: '#fff', color: '#ea580c', fontWeight: 600, cursor: 'pointer' }}>Demander</button>
                </div>
              )}
            </div>
          )}

          {/* Délai de relance paiement (facturation / attente de paiement) */}
          {slot.step === 'closing'
            && ['facturation', 'attente_paiement'].includes(slot.closing_step)
            && (relanceState || slot.payment_relance) && (
            <PaymentRelanceCard
              projectId={project.id}
              slotId={slot.id}
              state={relanceState || slot.payment_relance}
              canEdit={!!slot.can_set_payment_relance}
              onSaved={(next) => { setRelanceState(next); if (onUpdate) onUpdate(); }}
            />
          )}

          <PaymentRelancePrompt
            isOpen={relancePrompt}
            onClose={() => setRelancePrompt(false)}
            projectId={project.id}
            slotId={slot.id}
            itemName={slot.name}
            currentMode={relanceState?.mode}
            onSaved={(next) => { setRelanceState(next); if (onUpdate) onUpdate(); }}
          />

          {/* Notes */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <div onClick={() => setNotesOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: notesOpen ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={notesOpen ? faChevronDown : faChevronRight} size="xs" style={{ color: '#bdbdbd' }} />
                <FontAwesomeIcon icon={faStickyNote} size="xs" style={{ color: '#9e9e9e' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</span>
              </div>
              {localNotes && !notesOpen && <span style={{ fontSize: '0.65rem', color: '#bdbdbd' }}>•••</span>}
            </div>

            {notesOpen && (
              editingNotes ? (
                <div>
                  <textarea value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={3} autoFocus placeholder="Ajouter une note..."
                    style={{ width: '100%', fontSize: '0.78rem', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                    <button onClick={() => setEditingNotes(false)} disabled={savingNotes}
                      style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer' }}>Annuler</button>
                    <button onClick={handleSaveNotes} disabled={savingNotes}
                      style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', opacity: savingNotes ? 0.5 : 1 }}>
                      {savingNotes ? <FontAwesomeIcon icon={faSpinner} spin size="xs" /> : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {localNotes
                    ? <div style={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 6 }}>{localNotes}</div>
                    : <div style={{ fontSize: '0.72rem', color: '#d1d5db', marginBottom: 6 }}>Aucune note</div>}
                  <button onClick={() => { setNotesValue(localNotes); setEditingNotes(true); }}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={localNotes ? faEdit : faPlus} size="xs" className="me-1" />{localNotes ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              )
            )}
          </div>

          {/* Checklists */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px' }}>
            <div onClick={() => setChecklistOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: checklistOpen ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={checklistOpen ? faChevronDown : faChevronRight} size="xs" style={{ color: '#bdbdbd' }} />
                <FontAwesomeIcon icon={faClipboardList} size="xs" style={{ color: '#9e9e9e' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Checklist</span>
              </div>
              {checklists.length > 0 && <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9e9e9e' }}>{doneCount}/{checklists.length}</span>}
            </div>

            {checklistOpen && (
              <>
                {!checklistLoaded ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0', color: '#9e9e9e', fontSize: '0.78rem' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="sm" /> Chargement...
                  </div>
                ) : (
                  <>
                    {checklists.length === 0 && <div style={{ fontSize: '0.75rem', color: '#d1d5db', padding: '6px 0' }}>Aucun élément</div>}
                    {(() => {
                      const byStep = {};
                      checklists.forEach(item => { const s = item.step || 'autre'; if (!byStep[s]) byStep[s] = []; byStep[s].push(item); });
                      return Object.entries(byStep).map(([step, items]) => {
                        const accent = STEP_COLORS[step] || '#9e9e9e';
                        const stepDone = items.filter(i => i.is_completed).length;
                        return (
                          <div key={step} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, paddingBottom: 4, borderBottom: `1.5px solid ${accent}20` }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.62rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{STEP_LABELS[step] || step}</span>
                              </div>
                              <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#bdbdbd' }}>{stepDone}/{items.length}</span>
                            </div>
                            {items.map(item => (
                              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>
                                <div onClick={() => handleToggle(item.id)} style={{
                                  width: 16, height: 16, borderRadius: 3, flexShrink: 0, cursor: 'pointer',
                                  border: item.is_completed ? 'none' : '2px solid #d1d5db',
                                  background: item.is_completed ? '#dc2626' : '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
                                }}>
                                  {item.is_completed && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                </div>
                                <span style={{ flex: 1, fontSize: '0.78rem', color: item.is_completed ? '#bdbdbd' : '#242424', textDecoration: item.is_completed ? 'line-through' : 'none', fontWeight: item.is_completed ? 400 : 500 }}>
                                  {item.label}
                                </span>
                                {!(item.step === 'closing' && ['Livrables', 'Réception', 'Facturation', 'Encaissé', 'Attestation de référence'].includes(item.label)) && (
                                  <button onClick={() => setDeleteConfirm(item.id)}
                                    style={{ background: 'none', border: 'none', color: '#e0e0e0', cursor: 'pointer', padding: 2, transition: 'color 0.12s' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#d13438'} onMouseLeave={e => e.currentTarget.style.color = '#e0e0e0'}>
                                    <FontAwesomeIcon icon={faTrash} size="xs" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                      <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddChecklist(); }}
                        placeholder="Ajouter..." style={addInput} />
                      <button onClick={handleAddChecklist} disabled={!newLabel.trim() || adding}
                        style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: newLabel.trim() ? '#dc2626' : '#e5e7eb', color: newLabel.trim() ? '#fff' : '#bdbdbd', cursor: 'pointer', fontSize: '0.72rem' }}>
                        {adding ? <FontAwesomeIcon icon={faSpinner} size="xs" spin /> : <FontAwesomeIcon icon={faPlus} size="xs" />}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => { onClose(); navigate(`/projects/${project.id}/slots/${slot.id}`); }} style={detailBtn}>
            <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" /> Voir détails
          </button>
        </div>
      </div>
      {deleteConfirm && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...deleteDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: '24px 28px', width: 380, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h5 style={{ margin: '0 0 8px', fontSize: '0.92rem', fontWeight: 700, color: '#242424' }}>Supprimer cet élément ?</h5>
            <p style={{ fontSize: '0.78rem', color: '#757575', margin: '0 0 18px' }}>Cette action est irréversible.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#424242', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>Annuler</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes slotModalIn { from { opacity:0; transform:translateY(8px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  );
};

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' };
const modal = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden', animation: 'slotModalIn 0.2s ease' };
const closeBtn = { background: 'none', border: 'none', color: '#bdbdbd', cursor: 'pointer', padding: 6, borderRadius: 6 };
const counterChip = { display: 'flex', alignItems: 'center', gap: 8, background: '#fafafa', borderRadius: 8, padding: '10px 14px', transition: 'background 0.12s' };
const addInput = { flex: 1, fontSize: '0.75rem', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', background: '#fff' };
const detailBtn = { fontSize: '0.78rem', fontWeight: 600, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 };

export default SlotQuickModal;
